import React, { useState } from "react";
import { Modal } from "./Modal";
import { useGate } from "../Auth/gate";

export type GatedButtonProps = {
	/** Le jeton de permission exact que cette action exige (ex. "feature:deployment:write"). */
	token: string;
	onClick?: React.MouseEventHandler<HTMLButtonElement>;
	className?: string;
	/** Classes ajoutées PAR-DESSUS `className` quand la permission manque (défaut : grisé + interdit). */
	disabledClassName?: string;
	children?: React.ReactNode;
	title?: string;
	type?: "button" | "submit" | "reset";
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onClick" | "className" | "title" | "type" | "children">;

const DENIED_CLASSES = "opacity-50 grayscale cursor-not-allowed";

/**
 * Bouton gardé par UN jeton de permission. Autorisé → un <button> normal (tout passe au travers,
 * y compris `disabled`). Refusé → le bouton reste VISIBLE et cliquable mais grisé (même taille,
 * même place : rien ne saute), `aria-disabled`, et le clic ouvre une petite modale expliquant
 * quelle permission manque : bien plus honnête qu'un contrôle qui disparaît en silence. La
 * navigation, elle, reste masquée par le gating existant ; ceci ne concerne que les actions dans
 * la page. Le serveur applique de toute façon la même permission (403).
 */
const GatedButton: React.FC<GatedButtonProps> = ({
	token,
	onClick,
	className,
	disabledClassName,
	children,
	title,
	type = "button",
	disabled,
	...rest
}) => {
	const { allowed, label } = useGate(token);
	const [showDenied, setShowDenied] = useState(false);

	if (allowed) {
		return (
			<button type={type} onClick={onClick} className={className} title={title} disabled={disabled} {...rest}>
				{children}
			</button>
		);
	}

	return (
		<>
			{/* Volontairement PAS `disabled` : le clic doit ouvrir l'explication. */}
			<button
				type="button"
				aria-disabled="true"
				onClick={(e) => {
					// Les lignes de liste naviguent au clic : le bouton refusé ne doit jamais déclencher ça.
					e.preventDefault();
					e.stopPropagation();
					setShowDenied(true);
				}}
				className={`${className ?? ""} ${disabledClassName ?? DENIED_CLASSES}`}
				title={title}
				{...rest}
			>
				{children}
			</button>

			<Modal show={showDenied} onHide={() => setShowDenied(false)} size="sm" centered>
				<Modal.Header closeButton>
					<Modal.Title>Permission manquante</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p className="text-sm text-slate-700">
						Cette action nécessite la permission <b>{label}</b>. Contactez un administrateur pour l'obtenir.
					</p>
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						onClick={() => setShowDenied(false)}
						className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
					>
						Fermer
					</button>
				</Modal.Footer>
			</Modal>
		</>
	);
};

export default GatedButton;
